import { Pencil } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { Renderer, marked } from "marked";
import { useCallback, useEffect, useState } from "react";
import { Circle, ScrollView, YStack } from "tamagui";

import {
  getGetClinicRequestQueryKey,
  useGetSubjectiveSharebackForConversation,
  useTransitionAnAsyncClinicRequestToAFullVisit,
  useUpdateSubjectiveSharebackForConversation,
} from "@/api/generated";
import { ProgressionStep } from "@/api/generated/models";
import { Button, Footer, Header, Skeleton, TextArea } from "@/components";
import { Heading, Paragraph } from "@/components/Typography";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";

// Poll while the backend is regenerating a stale subjective (`refreshing`). See AI-1917.
const REFRESH_POLL_INTERVAL_MS = 2000;

type SubjectiveSection = {
  label: string;
  value: string;
};

const plainTextRenderer = new Renderer();
plainTextRenderer.text = function (token) {
  return "tokens" in token && token.tokens
    ? this.parser.parseInline(token.tokens)
    : token.text;
};
plainTextRenderer.paragraph = function ({ tokens }) {
  return `${this.parser.parseInline(tokens)}\n`;
};
plainTextRenderer.code = function ({ text }) {
  return `${text}\n`;
};
plainTextRenderer.blockquote = function ({ tokens }) {
  return `${this.parser.parse(tokens).trim()}\n`;
};
plainTextRenderer.heading = function ({ tokens }) {
  return `${this.parser.parseInline(tokens)}\n`;
};
plainTextRenderer.hr = function () {
  return "\n";
};
plainTextRenderer.html = function ({ text }) {
  return text.replace(/<[^>]*>/g, "");
};
plainTextRenderer.table = function ({ header, rows }) {
  const renderRow = (cells: typeof header) =>
    cells
      .map((cell) => this.parser.parseInline(cell.tokens).trim())
      .join(" | ");
  return `${[renderRow(header), ...rows.map(renderRow)].join("\n")}\n`;
};
plainTextRenderer.checkbox = function ({ checked }) {
  return checked ? "[x] " : "[ ] ";
};
plainTextRenderer.list = function ({ items }) {
  return `${items
    .map((item) => `- ${this.parser.parse(item.tokens).trim()}`)
    .join("\n")}\n`;
};
plainTextRenderer.strong = function ({ tokens }) {
  return this.parser.parseInline(tokens);
};
plainTextRenderer.em = function ({ tokens }) {
  return this.parser.parseInline(tokens);
};
plainTextRenderer.del = function ({ tokens }) {
  return this.parser.parseInline(tokens);
};
plainTextRenderer.codespan = function ({ text }) {
  return text;
};
plainTextRenderer.link = function ({ tokens }) {
  return this.parser.parseInline(tokens);
};
plainTextRenderer.image = function ({ text }) {
  return text;
};
plainTextRenderer.br = function () {
  return "\n";
};

const markdownToPlainText = (markdown: string): string =>
  marked.parse(markdown, { renderer: plainTextRenderer, async: false }).trim();

// Split the shareback into sections on markdown headings, preserving any text
// before the first heading as a Summary section.
export const parseMarkdownSections = (text: string): SubjectiveSection[] => {
  const sections: SubjectiveSection[] = [];

  let current: SubjectiveSection = { label: "Summary", value: "" };
  const flush = () => {
    if (current.value.trim()) {
      sections.push({
        label: current.label,
        value: markdownToPlainText(current.value),
      });
    }
  };

  for (const token of marked.lexer(text)) {
    if (token.type === "heading") {
      flush();
      current = { label: token.text.trim(), value: "" };
    } else if (current) {
      current.value += token.raw;
    }
  }
  flush();

  return sections;
};

export const sectionsToMarkdown = (sections: SubjectiveSection[]): string => {
  return sections.map((s) => `### ${s.label}\n${s.value}`).join("\n\n");
};

const getSubjectiveSharebackText = (
  subjectiveShareback: { data?: unknown; text?: unknown } | undefined,
): string | undefined => {
  if (typeof subjectiveShareback?.text === "string") {
    return subjectiveShareback.text;
  }

  if (
    subjectiveShareback?.data &&
    typeof subjectiveShareback.data === "object" &&
    "text" in subjectiveShareback.data &&
    typeof subjectiveShareback.data.text === "string"
  ) {
    return subjectiveShareback.data.text;
  }

  return undefined;
};

const getRowCount = (text: string, minRows = 2): number => {
  const newlineCount = (text.match(/\n/g) || []).length;

  if (newlineCount === 0) {
    return minRows;
  } else {
    return Math.max(newlineCount + 1, minRows) + 2;
  }
};

type SubjectiveProps = {
  onClose?: () => void;
  conversationId?: string;
  onContinue?: () => void;
  clinicRequestId?: string;
};

const Subjective = ({
  onClose,
  conversationId,
  onContinue,
  clinicRequestId,
}: SubjectiveProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToastController();
  const progressionContext = useUserProgression();
  const { data: subjectiveShareback, isLoading: isSubjectiveSharebackLoading } =
    useGetSubjectiveSharebackForConversation(conversationId ?? "", {
      query: {
        enabled: !!conversationId,
        refetchInterval: (query) =>
          query.state.data?.refreshing ? REFRESH_POLL_INTERVAL_MS : false,
      },
    });
  const isRefreshing = !!subjectiveShareback?.refreshing;
  const { mutateAsync: updateSubjectiveSharebackAsync, isPending } =
    useUpdateSubjectiveSharebackForConversation();
  const { mutateAsync: transitionToVisitAsync, isPending: isTransitioning } =
    useTransitionAnAsyncClinicRequestToAFullVisit();

  const [sections, setSections] = useState<SubjectiveSection[]>([]);
  const [edited, setEdited] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [transitioned, setTransitioned] = useState(false);

  const isAsyncFlow = progressionContext?.progression.steps.includes(
    ProgressionStep.async_visit,
  );
  const isReadonly = isAsyncFlow && !isEditing;

  const handleProgression = useCallback(() => {
    onContinue?.();
  }, [onContinue]);

  useEffect(() => {
    // Hold off parsing while a fresher summary is still generating so we never surface the stale
    // (pre-registration) text to the patient.
    if (isRefreshing) {
      return;
    }
    const text = getSubjectiveSharebackText(subjectiveShareback);
    if (text) {
      if (text === "None generated per workflow") {
        handleProgression();
        return;
      }
      setSections(parseMarkdownSections(text));
    }
  }, [subjectiveShareback, handleProgression, isRefreshing]);

  const handleSectionChange = (index: number, value: string) => {
    setSections((prev) =>
      prev.map((section, i) => (i === index ? { ...section, value } : section)),
    );
    setEdited(true);
  };

  const handleTransition = async () => {
    const updatedText = sectionsToMarkdown(sections);
    try {
      await updateSubjectiveSharebackAsync({
        conversationId: conversationId ?? "",
        data: { text: updatedText },
      });
      await transitionToVisitAsync({ clinicRequestId: clinicRequestId! });
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: getGetClinicRequestQueryKey(
            conversationId,
            clinicRequestId!,
          ),
        });
      }
      setTransitioned(true);
    } catch {
      showToast("Something went wrong. Please try again.");
    }
  };

  const handleContinue = async () => {
    // The transition voids this progression's clinic request — advancing it buys the wrong product.
    if (transitioned) {
      onClose?.();
      return;
    }

    if (!edited) {
      handleProgression();
      return;
    }

    if (isAsyncFlow) {
      await handleTransition();
      return;
    }

    const updatedText = sectionsToMarkdown(sections);
    try {
      await updateSubjectiveSharebackAsync({
        conversationId: conversationId ?? "",
        data: { text: updatedText },
      });
    } finally {
      handleProgression();
    }
  };

  const buttonLabel = () => {
    if (isPending || isTransitioning) return "Saving...";
    if (isAsyncFlow && edited && !transitioned) return "Save changes";
    return "Continue";
  };

  const stickyFooter = (
    <Footer padding="$200" mode="dialog">
      <Button
        mode="primary"
        constrained
        flex={1}
        onPress={handleContinue}
        disabled={isPending || isTransitioning || isRefreshing}
        dataTrackingId={
          edited
            ? "saved-subjective-shareback"
            : "subjective-shareback-continue"
        }
      >
        {buttonLabel()}
      </Button>
    </Footer>
  );

  if (transitioned) {
    return (
      <>
        <ScrollView
          flex={1}
          contentContainerStyle={{
            paddingHorizontal: "$200",
            paddingBottom: "$200",
          }}
        >
          <Header gap="$100">
            <Heading size="$xl">We'll have a clinician review</Heading>
            <Paragraph size="$sm" color="$textSecondary">
              Based on your changes, we'd like a clinician to review your case.
              Continue to get connected.
            </Paragraph>
          </Header>
        </ScrollView>
        {stickyFooter}
      </>
    );
  }

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingHorizontal: "$200",
          paddingBottom: "$200",
        }}
      >
        <Header gap="$100">
          <Heading size="$xl">Here's what we've heard</Heading>
          <Paragraph size="$sm" color="$textSecondary">
            {isReadonly
              ? "Review the details below to make sure your clinician has the most accurate information."
              : "Review the details below to make sure your clinician has the most accurate information. You can edit anything before we send it."}
          </Paragraph>
        </Header>

        <YStack gap="$200" marginTop="$200">
          {isSubjectiveSharebackLoading || isRefreshing
            ? Array.from({ length: 5 }).map((_, index) => {
                return (
                  <YStack key={index} gap="$100">
                    <Skeleton.Text />
                    <Skeleton height="$700" />
                  </YStack>
                );
              })
            : sections.map((section, index) => {
                const inputId = `subjective-textarea-${index}`;

                return (
                  <YStack key={`${section.label}-${index}`} gap="$100">
                    <TextArea
                      id={inputId}
                      label={section.label}
                      value={section.value}
                      rows={getRowCount(section.value)}
                      readonly={isReadonly}
                      onChangeText={
                        isReadonly
                          ? undefined
                          : (text) => handleSectionChange(index, text)
                      }
                    />
                    {isReadonly && (
                      <Circle
                        size="$350"
                        backgroundColor="$fillModal"
                        borderColor="$strokeNeutral"
                        borderWidth="$strokeWidth"
                        elevation={2}
                        position="absolute"
                        bottom={-12}
                        right={-4}
                        zIndex={1}
                        onPress={() => setIsEditing(true)}
                        aria-label={`Edit ${section.label}`}
                        cursor="pointer"
                      >
                        <Pencil size={14} color="$textLink" />
                      </Circle>
                    )}
                  </YStack>
                );
              })}
        </YStack>
      </ScrollView>
      {stickyFooter}
    </>
  );
};

export default Subjective;
